import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";

import ScopeCertificateComponent from "./sc-tabs/scope-certificate-details";
import FacilityDetailsComponent from "./sc-tabs/facility-details";
import ProductManagementComponent from "./sc-tabs/facility-product-details";
import CertifiedOrganizationDetails from "./sc-tabs/certified-organization-details/certified-organization-details";
import ProductCategoryInformationTab from "./sc-tabs/product-category-information";
// ProductDetailInformationTab merged into ProductCategoryInformationTab
import RawMaterialDetailsTab from "./sc-tabs/raw-material-details";
import ComponentClaimsCategoryDetailsTab from "./sc-tabs/component-claims-category-details";
import { useParams } from "react-router-dom";
import CustomizedTabs from "../../../../shared/components/navigations/tabs";
import { useApi } from "../../../../shared/hooks/useApi";
import { API_ROUTES } from "../../../../shared/app-configs/sc-app-config";
import { useScStore } from "../../../../store/scStandard.store";
import { useSCDataPrefetch } from "./hooks/useSCDataPrefetch";
import { computeCrossTabErrors } from "./utils/crossTabValidation";
import { scCacheService } from "./utils/scCacheService";

const ScForm = () => {
  // Retrieve URL parameters.
  const { formtype, SC_ID } = useParams<{ formtype: string; SC_ID: string }>();
  const baseUrl = formtype ?? "create-sc";
  const enableDisableLogic : Boolean= formtype === 'submited-sc' ? false:true;

  // We'll store the API response in an object: { [tabId]: boolean }
  // For example: { "scope-certificate-details": true, "certified-organization-details": false, ... }
  const [completionMap, setCompletionMap] = useState<Record<string, boolean>>({});
  
  // Store the SC status from API response
  const [scStatus, setScStatus] = useState<string>("");

  // Track whether the Facility Information tab has at least one facility.
  // null  = not yet known (data still loading)
  // true  = ≥1 facility exists → downstream tabs allowed
  // false = 0 facilities → downstream tabs disabled + highlighted yellow
  const [hasFacilities, setHasFacilities] = useState<boolean | null>(null);

  // Read per-tab error flags + data for cross-tab validation.
  const { tabErrors, scStandards, facilityDetailsCache, savedClaims, reviewedFacilities } = useScStore();

  // Background prefetch: populates Zustand (L1) + localStorage (L2) with all
  // validation-relevant data immediately on SC load — non-blocking.
  useSCDataPrefetch(SC_ID);

  // If you have a custom API hook, initialize it here.
  // Otherwise, you can use fetch/axios directly.
  const { loading, executeGet } = useApi();

  /**
   * Fetch the completion data from your API.
   * For instance, if your endpoint is "v1/sc/get-disable-tabs" and returns an object like:
   * {
   *   "scope-certificate-details": true,
   *   "certified-organization-details": true,
   *   "facility-details": false,
   *   "facility-product-details": false
   * }
   * where `true` means "that tab is completed."
   */
  const fetchCompletionData = async () => {
    if (!SC_ID) return; // If there's no SC_ID, skip the call
    try {
      // Use the v4 API endpoint for getting tab completion status
      const response: any = await executeGet(API_ROUTES.GET_DISABLE_TABS_V4, {
        scId: SC_ID,
      });
      setCompletionMap(response.data || {});
      
      // Extract SC status from response
      if (response.data && response.data["sc-status"]) {
        setScStatus(response.data["sc-status"]);
      }
    } catch (error) {
      console.error("[ScForm] Error fetching tab completion status:", error);
    }
  };

  useEffect(() => {
    const store = useScStore.getState();

    // ── SESSION START: wipe all transient L1 caches immediately ──────────────
    // This guarantees that switching from SC-A → SC-B (or creating a new SC
    // from any existing-SC context) never sees stale data from the previous
    // session, even if the previous cleanup didn't run (e.g. hot-reload, crash).
    store.clearAllTransientState();

    // If the persisted reviewedFacilities belong to a *different* SC_ID, nuke them.
    // This happens when the user navigates to a different SC without a full
    // page reload (the Zustand persist store survives but SC_ID has changed).
    if (SC_ID && store.persistedScId && store.persistedScId !== SC_ID) {
      store.clearReviewedFacilities();
    }
    // Record which SC this session belongs to (persisted across page refreshes)
    if (SC_ID) {
      store.setPersistedScId(SC_ID);
      // Also clear L2 localStorage for this SC at start of session so we always
      // read fresh data from the API, not 30-min-old cached data from a previous edit.
      scCacheService.clearAll(SC_ID);
    }

    fetchCompletionData();

    return () => {
      // SESSION END cleanup: clear L1 Zustand store and L2 localStorage for this SC.
      store.clearAllTransientState();
      if (SC_ID) scCacheService.clearAll(SC_ID);
    };
  }, [SC_ID]);

  // ── Cross-tab error computation (pure, memoized) ──────────────────────────
  // Delegates to the pure computeCrossTabErrors utility so this file stays thin.
  // Recomputes whenever Zustand Prefetch populates facilityDetailsCache or savedClaims
  // (which happens in the background shortly after mount).
  const proactiveTabErrors = useMemo(
    () => computeCrossTabErrors({ scStandards, facilityDetailsCache, savedClaims, reviewedFacilities }),
    [scStandards, facilityDetailsCache, savedClaims, reviewedFacilities]
  );

  /**
   * Called when user clicks "Next" in any tab, so we can re-fetch
   * the latest completion data.
   */
  const handleNextTab = async () => {
    console.log("[ScForm] handleNextTab triggered. Re-fetching completion data.");
    await fetchCompletionData();
  };

  /**
   * Helper to see if a given tab is completed.
   */
  const isTabCompleted = (tabId: string) => {
    if (tabId === "product-category-information") {
      return !!completionMap["product-category-information"] && !!completionMap["product-detail-information"];
    }
    return !!completionMap[tabId];
  };

  /**
   * Helper to determine if tabs should be read-only based on SC status.
   * Returns true if SC status is Suspended (S), Withdrawn (W), or Expired (E).
   */
  const isReadOnlyBasedOnStatus = () => {
    return scStatus === "S" || scStatus === "W" || scStatus === "E";
  };

  // Define all tabs as mandatory in a specific order.
  const mandatoryTabs = [
    "scope-certificate-details",
    "certified-organization-details",
    "facility-details",
    "product-category-information",
    "raw-material-details",
    "component-details",
  ];

  // Build the base tabs array.
  const baseTabs = [
    {
      id: "scope-certificate-details",
      label: "Scope Certificate Information",
      component: <ScopeCertificateComponent onNextTab={handleNextTab} />,
    },
    {
      id: "certified-organization-details",
      label: "Certified Organization Information",
      component: <CertifiedOrganizationDetails onNextTab={handleNextTab} scStatus={isReadOnlyBasedOnStatus()} />,
    },
    {
      id: "facility-details",
      label: "Facility Information",
      component: <FacilityDetailsComponent
        onNextTab={handleNextTab}
        scStatus={isReadOnlyBasedOnStatus()}
        onFacilitiesChange={setHasFacilities}
      />,
    },
    {
      id: "product-category-information",
      label: "Product Category & Detail Information",
      component: <ProductCategoryInformationTab onNextTab={handleNextTab} scStatus={isReadOnlyBasedOnStatus()} />,
    },
    {
      id: "raw-material-details",
      label: "Raw Material Information",
      component: <RawMaterialDetailsTab onNextTab={handleNextTab} scStatus={isReadOnlyBasedOnStatus()} />,
    },
    {
      id: "component-details",
      label: "Component & Claims Category Information",
      component: <ComponentClaimsCategoryDetailsTab scStatus={isReadOnlyBasedOnStatus()} />,
    },
    
    // {
    //   id: "facility-product-details",
    //   label: "Product Details",
    //   component: <ProductManagementComponent completionMap={completionMap} onNextTab={handleNextTab}/>,
    // },
  ];

  // Check if SC_ID is present.
  const hasSC_ID = Boolean(SC_ID);

  // Compute the final tabs array with or without disable logic.
  const facilityTabIndex = mandatoryTabs.indexOf('facility-details');

  const tabs = baseTabs.map((tab) => {
    // If disable logic is turned off, enable all tabs.
    if (!enableDisableLogic) {
      return { ...tab, disabled: false };
    }

    // Otherwise, apply mandatory logic.
    let disabled = false;

    // The first tab is always enabled.
    if (tab.id === "scope-certificate-details") {
      disabled = false;
    } else {
      // For subsequent tabs, we need SC_ID and all previous mandatory tabs to be completed.
      if (!hasSC_ID) {
        disabled = true;
      } else {
        // Find the index of the current tab in the mandatoryTabs array.
        const currentIndex = mandatoryTabs.indexOf(tab.id);
        if (currentIndex > 0) {
          // If any previous mandatory tab is not completed, disable this one.
          for (let i = 0; i < currentIndex; i++) {
            if (!isTabCompleted(mandatoryTabs[i])) {
              disabled = true;
              break;
            }
          }
        }
      }
    }

    // If any tab before the current tab has a validation error, disable the current tab
    const currentIndex = mandatoryTabs.indexOf(tab.id);
    if (currentIndex > 0 && !disabled) {
      let hasPreviousTabError = false;
      for (let i = 0; i < currentIndex; i++) {
        const prevTabId = mandatoryTabs[i];
        const hasError = Boolean(tabErrors[prevTabId]) || Boolean(proactiveTabErrors[prevTabId]);
        if (hasError) {
          hasPreviousTabError = true;
          break;
        }
      }
      if (hasPreviousTabError) {
        disabled = true;
      }
    }

    // warning = true on a tab when:
    //   a) all facilities were deleted and this tab is downstream (no-facility case), OR
    //   b) the tab itself has signalled an active cross-tab error via Zustand tabErrors.
    // warning tabs are NOT disabled — they remain fully clickable.
    const thisTabIndex = mandatoryTabs.indexOf(tab.id);
    const isAfterFacilityTab = thisTabIndex > facilityTabIndex;
    const noFacilities = hasFacilities === false; // null = unknown, don't warn yet
    // warning = true when EITHER:
    //   a) The tab itself has mounted and reported an error via Zustand tabErrors, OR
    //   b) The parent has proactively computed an error from Zustand state
    //      (allows multiple pills to glow orange even before the user visits those tabs).
    const hasTabError = Boolean(tabErrors[tab.id]) || Boolean(proactiveTabErrors[tab.id]);
    const warning = (isAfterFacilityTab && noFacilities) || hasTabError;

    return { ...tab, disabled, warning };
  });

  return (
    <>
      <Box sx={{ mt: "2px" }}>
        <CustomizedTabs tabs={tabs} baseUrl={baseUrl} system="v4/sc" ID={SC_ID} />
      </Box>
    </>
  );
};

export default ScForm;
